import clsx from 'clsx'
import { ReactComponent as AutomaticSvg } from 'images/icons/cleaning.svg'
import { ReactComponent as EmergencySvg } from 'images/icons/emergency.svg'
import { ReactComponent as FirmwareUpdateSvg } from 'images/icons/firmware_update.svg'
import { ReactComponent as ManualSvg } from 'images/icons/joystick.svg'
import { ReactComponent as LocationSvg } from 'images/icons/location.svg'
import { ReactComponent as MappingSvg } from 'images/icons/map-bold.svg'
import { ReactComponent as ParkingSvg } from 'images/icons/parking.svg'
import { ReactComponent as PauseSvg } from 'images/icons/pause.svg'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import { ReactComponent as RoutingSvg } from 'images/icons/route.svg'
import { ReactComponent as IdleSvg } from 'images/icons/sand_clock.svg'
import { ReactComponent as WashingSvg } from 'images/icons/shower.svg'
import { ReactComponent as UpdateSvg } from 'images/icons/update.svg'
import { ROBOT_COMMANDS, ROBOT_MODES, ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'

import style from './RobotModeIcons.module.css'

interface Props {
  robot: IRobotInfo
  large?: boolean
  hideEmergency?: boolean
  hidePause?: boolean
}

const RobotModeIcons = ({ robot, large, hideEmergency, hidePause }: Props) => {
  const isOnline = robot.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE
  if (!isOnline) return null

  const activeMode = robot.activeMode

  const renderModeIcon = () => {
    let Icon = null
    let title = ''

    switch (activeMode) {
      case ROBOT_MODES.MODE_NOT_SET:
        Icon = IdleSvg
        title = 'Waiting for a task'
        break
      case ROBOT_MODES.MODE_MANUAL:
        Icon = ManualSvg
        title = 'Manual Mode'
        break
      case ROBOT_MODES.MODE_AUTOMATIC:
        Icon = AutomaticSvg
        title = `Working Mode${robot.job?.missionName ? `\nMission: ${robot.job.missionName}` : ''}`
        break
      case ROBOT_MODES.MODE_PARKING:
        Icon = ParkingSvg
        title = 'Parking Mode'
        break
      case ROBOT_MODES.MODE_CHARGING:
        Icon = ChargingSvg
        title = 'Going to the charging station'
        break
      case ROBOT_MODES.MODE_WASHING:
        Icon = WashingSvg
        title = 'Washing'
        break
      case ROBOT_MODES.MODE_MAPPING:
        Icon = MappingSvg
        title = 'Mapping'
        break
      case ROBOT_MODES.MODE_ROUTE_RECORDING:
        Icon = RoutingSvg
        title = 'Route Recording'
        break
      case ROBOT_MODES.MODE_CALIBRATION_PARKING:
      case ROBOT_MODES.MODE_CALIBRATION_CHARGING:
      case ROBOT_MODES.MODE_CALIBRATION_WASHING:
        Icon =
          activeMode === ROBOT_MODES.MODE_CALIBRATION_PARKING
            ? ParkingSvg
            : activeMode === ROBOT_MODES.MODE_CALIBRATION_CHARGING
            ? ChargingSvg
            : WashingSvg
        title = 'Calibration'
        break
      case ROBOT_MODES.MODE_UPDATING_FIRMWARE:
        Icon = FirmwareUpdateSvg
        title = 'Firmware Updating'
        break
      case ROBOT_MODES.MODE_REBOOT_ROBOT:
        Icon = UpdateSvg
        title = 'Robot Rebooting'
        break
      case ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION:
      case ROBOT_MODES.MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION:
      case ROBOT_MODES.MODE_SWITCHING_TO_COMBINED_LOCALIZATION:
        Icon = LocationSvg
        title = 'Switching localization mode'
        break
      default:
        return null
    }

    if (!Icon) return null

    return (
      <div className={style.statusItem} title={title}>
        <Icon
          className={clsx(
            style.modeIcon,
            large && style.largeIcon,
            Icon === IdleSvg && style.sandClockIcon,
            large && Icon === IdleSvg && style.largeSandClockIcon
          )}
        />
      </div>
    )
  }

  const renderCommandIcon = () => {
    if (!hideEmergency && robot.emergencyButton) {
      return (
        <div className={style.statusItem} title="Emergency Stop">
          <EmergencySvg className={clsx(style.statusIcon, style.emergencyIcon)} />
        </div>
      )
    }

    if (!hidePause && robot.activeCommand === ROBOT_COMMANDS.PAUSE && robot.activeMode === ROBOT_MODES.MODE_AUTOMATIC) {
      return (
        <div className={style.statusItem} title="Paused">
          <PauseSvg className={style.statusIcon} style={{ color: '#4680fdff' }} />
        </div>
      )
    }

    return null
  }

  return (
    <div className={clsx(style.statusContainer, large && style.largeContainer)}>
      {renderModeIcon()}
      {renderCommandIcon()}
    </div>
  )
}

export default RobotModeIcons
